export function getPropertyByPath<T, P1 extends keyof T, P2 extends keyof T[P1]>(object: T, path: [P1, P2]): T[P1][P2];
export function getPropertyByPath<T, P extends keyof T>(object: T, path: [P]): T[P];
export function getPropertyByPath<T>(object: T, path: []): T;
export function getPropertyByPath(object: unknown, path: (string | number)[]): unknown;
/**
 * Returns a value at the given path, in a null-safe way.
 * @param object object to get a property from
 * @param path the path to a nested property
 * @returns the value at the given path
 */
export function getPropertyByPath(object: any, path: (string | number)[]): any {
  return path.reduce((value, key) => value?.[key], object);
}

export function setPropertyByPath<T>(object: T, path: (string | number)[], value: any): T {
  // NOTE: Does not mutate the path but creates a copy
  if (!path.length) {
    return value;
  }
  const [key, ...nestedPath] = path;
  if (object == null || typeof object !== 'object') {
    throw new Error(`Cannot set property "${key}" of value ${object}`);
  }
  if (Array.isArray(object)) {
    const index = key as number;
    const array = object.slice();
    array[index] = setPropertyByPath(array[index], nestedPath, value);
    return array as T;
  }
  return {
    ...object,
    [key]: setPropertyByPath((object as any)[key], nestedPath, value),
  };
}

export type OmitUndefined<T> = {
  [P in keyof T]: Exclude<T[P], undefined>;
};

export function omitUndefinedProperties<T extends object>(object: T): OmitUndefined<T> {
  const result: Record<string, unknown> = {};
  Object.entries(object).forEach(([key, value]) => {
    if (typeof value !== 'undefined') {
      result[key] = value;
    }
  });
  return result as OmitUndefined<T>;
}

export function getUniquePropertyName(baseName: string, object: Record<string, unknown>, suffix?: number): string {
  const candidate = suffix != null ? `${baseName}${suffix}` : baseName;
  if (object[candidate] == null) {
    return candidate;
  }
  return getUniquePropertyName(baseName, object, (suffix ?? 1) + 1);
}
